import { omit } from 'lodash';
import { apolloClient } from '~/apollo-client';
import {
  Flex_FetchFunnelPacketQuery,
  Flex_FetchFunnelPacketQueryVariables,
  UserOriginationSource,
} from '~/typings/graphql';
import { UpdateUserMetaDataParams } from '../update-user-meta-data';
import { FETCH_FUNNEL_PACKET } from './fetch-funnel-packet.queries';

export const fetchFunnelPacket = async (
  params: FetchFunnelPacketParams,
): Promise<FetchFunnelPacketResponse> => {
  const { data } = await apolloClient.query<
    Flex_FetchFunnelPacketQuery,
    Flex_FetchFunnelPacketQueryVariables
  >({
    query: FETCH_FUNNEL_PACKET,
    variables: {
      accountId: params.accountId,
      customPlanId: params.customPlanId,
      userId: params.userId,
      utilityAccountId: params.utilityAccountId,
    },
    fetchPolicy: 'no-cache',
  });

  return {
    nonLmi: data.funnelPacket.nonLmi,
    inviteCode: data.funnelPacket.inviteCode,
    account: data.funnelPacket.utilityAccount.account,
    funnelVnmProjectId: data.funnelPacket.funnelVnmProjectId,
    customPlan: omit(data.funnelPacket.customPlan, ['planTemplate']),
    planTemplate: data.funnelPacket.customPlan.planTemplate,
    signedContractIds: data.funnelPacket.signedContractIds,
    utilityAccount: omit(data.funnelPacket.utilityAccount, [
      'account',
      'utilityCredential',
      'user',
      'utility',
    ]),
    utility: data.funnelPacket.utilityAccount.utility,
    utilityCredential: data.funnelPacket.utilityAccount.utilityCredential,
    user: data.funnelPacket.utilityAccount.user,
    unparsedQueryParams: data.funnelPacket.unparsedQueryParams,
  };
};

export type FetchFunnelPacketParams = {
  accountId?: number;
  customPlanId?: number;
  userId: number;
  utilityAccountId?: number;
};

type Packet = Flex_FetchFunnelPacketQuery['funnelPacket'];
export interface FetchFunnelPacketResponse {
  account: Packet['utilityAccount']['account'];
  customPlan: Omit<Packet['customPlan'], 'planTemplate'>;
  planTemplate: Packet['customPlan']['planTemplate'];
  signedContractIds: Packet['signedContractIds'];
  nonLmi: Packet['nonLmi'];
  inviteCode: Packet['inviteCode'];
  user: FunnelPacketUser;
  funnelVnmProjectId?: number | null;
  utility: Packet['utilityAccount']['utility'];
  utilityAccount: Omit<
    Packet['utilityAccount'],
    'account' | 'user' | 'utility' | 'utilityCredential'
  >;
  utilityCredential: Packet['utilityAccount']['utilityCredential'];
  unparsedQueryParams?: Packet['unparsedQueryParams'];
  utilityAccountMissingError?: Packet['utilityAccount']['utilityAccountMissingError'];
}

type FunnelPacketUser = {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phoneNumber?: string | null;
  zipCode?: string | null | undefined;
  promoCode?: string | undefined;
  phone?: string | null;
  metaData?: UpdateUserMetaDataParams;
  originationSource?: FunnelPacketUserOriginationSource | null;
  signupPromoCode?: { code: string } | null | undefined;
  nonLmi?: boolean;
  inviteCode?: string;
};

export interface FunnelPacketUserOriginationSource
  extends Pick<
    UserOriginationSource,
    | 'funnel'
    | 'originationSourceType'
    | 'queryParams'
    | 'funnelVnmProjectId'
    | 'enrollmentExperience'
  > {}
